import React, { useEffect, useMemo, useState } from "react";
import { useSectionSubmit } from "../lib/useSectionSubmit";
import LabeledInput from "../components/LabeledInput";
import { useAuthBootstrap } from "../../../context/AuthBootstrapContext";

function toDateInputValue(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

export default function BasicDetailsSection() {
  const { person, refreshBootstrap } = useAuthBootstrap();

  const { status, error, submit } = useSectionSubmit({
    endpoint: "/api/settings/basic-details",
  });

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    birthday: "",
    city: "",
    zipCode: "",
    neighborhood: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!person) return;

    setForm({
      firstName: person.first_name || "",
      lastName: person.last_name || "",
      email: person.email || "",
      birthday: toDateInputValue(person.birthday),
      city: person.city || "",
      zipCode: person.zip_code || "",
      neighborhood: person.neighborhood || "",
    });
  }, [person]);

  const canSave = status !== "saving";

  const validate = useMemo(() => {
    return (values) => {
      const errs = {};
      if (!values.firstName.trim()) errs.firstName = "First name is required.";
      if (!values.lastName.trim()) errs.lastName = "Last name is required.";
      if (!values.email.trim()) errs.email = "Email is required.";
      return errs;
    };
  }, []);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  async function onSave(e) {
    e.preventDefault();

    const errs = validate(form);
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    const result = await submit({
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      email: form.email.trim(),
      birthday: form.birthday || null,
      city: form.city.trim(),
      zip_code: form.zipCode.trim(),
      neighborhood: form.neighborhood.trim(),
    });

    if (result?.ok && typeof refreshBootstrap === "function") {
      await refreshBootstrap();
    }
  }

  return (
    <form className="sectionForm" onSubmit={onSave}>
      <div className="grid2">
        <LabeledInput
          label="First Name"
          name="firstName"
          value={form.firstName}
          onChange={(v) => updateField("firstName", v)}
          placeholder="First Name"
          required
          error={fieldErrors.firstName}
        />

        <LabeledInput
          label="Last Name"
          name="lastName"
          value={form.lastName}
          onChange={(v) => updateField("lastName", v)}
          placeholder="Last Name"
          required
          error={fieldErrors.lastName}
        />
      </div>

      <LabeledInput
        label="Email"
        name="email"
        value={form.email}
        onChange={(v) => updateField("email", v)}
        placeholder="Email"
        type="email"
        autoComplete="email"
        required
        error={fieldErrors.email}
      />

      <div className="sectionDivider" />

      <div className="sectionSubhead">I’m currently in:</div>

      <LabeledInput
        label="City"
        name="city"
        value={form.city}
        onChange={(v) => updateField("city", v)}
        placeholder="City"
        autoComplete="address-level2"
      />

      <LabeledInput
        label="Zip Code"
        name="zipCode"
        value={form.zipCode}
        onChange={(v) => updateField("zipCode", v)}
        placeholder="Zip Code"
        autoComplete="postal-code"
      />

      <LabeledInput
        label="Neighborhood"
        name="neighborhood"
        value={form.neighborhood}
        onChange={(v) => updateField("neighborhood", v)}
        placeholder="e.g. Silver Lake, Williamsburg, Midtown"
        autoComplete="off"
      />

      <div className="sectionActions">
        <button className="btnPrimary" type="submit" disabled={!canSave}>
          {status === "saving" ? "Saving…" : "Save changes"}
        </button>

        {status === "saved" ? <span className="saveHint">Saved</span> : null}
      </div>

      {error ? <div className="formError">{error}</div> : null}
    </form>
  );
}